import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Send, Users, Tag, ListFilter, FlaskConical, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Guest, SegmentCondition, Survey } from '@core/models';

interface SendPayload {
  type: string;
  title: string;
  message: string;
  message_type?: string;
  image_url?: string;
  tags?: string[];
  flex_message?: string;
  attribute_filters?: SegmentCondition[];
  delivery_target_item_ids?: string[];
  scheduled_at?: string;
}

interface NotificationTag {
  id: string;
  name: string;
  description: string;
  color: string;
  is_active: boolean;
}

interface DeliveryTargetItemNode {
  id: string;
  type_id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
}

interface DeliveryTargetTypeNode {
  id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  items: DeliveryTargetItemNode[];
}

interface AttributeFilterRow {
  key: string;
  values: string;
  match: string;
}

const NotificationSend: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [tags, setTags] = useState<NotificationTag[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [deliveryTargets, setDeliveryTargets] = useState<DeliveryTargetTypeNode[]>([]);
  const [sending, setSending] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [guestSearch, setGuestSearch] = useState('');
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'broadcast',
    title: '',
    message: '',
    message_type: 'text',
    image_url: '',
    flex_message: '',
    scheduled_at: '',
    tags: [] as string[],
    delivery_target_item_ids: [] as string[],
    filters: [] as AttributeFilterRow[],
  });

  const fetchTags = useCallback(async () => {
    if (!tenantStore.selectedTenant?.id) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notification-tags`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTags(data.filter((tag: NotificationTag) => tag.is_active) || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  }, [tenantStore.selectedTenant?.id]);

  const fetchSurveys = useCallback(async () => {
    if (!tenantStore.selectedTenant?.id) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/surveys`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data: Survey[] = await response.json();
        setSurveys(data.filter((survey) => survey.is_active && survey.questions.length > 0));
      }
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
    }
  }, [tenantStore.selectedTenant?.id]);

  const fetchDeliveryTargets = useCallback(async () => {
    if (!tenantStore.selectedTenant?.id) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/delivery-targets`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data: DeliveryTargetTypeNode[] = await response.json();
        setDeliveryTargets(
          data
            .filter((type) => type.is_active)
            .map((type) => ({
              ...type,
              items: (type.items || []).filter((item) => item.is_active),
            }))
            .filter((type) => type.items.length > 0)
        );
      }
    } catch (error) {
      console.error('Failed to fetch delivery targets:', error);
    }
  }, [tenantStore.selectedTenant?.id]);

  useEffect(() => {
    fetchTags();
    fetchSurveys();
    fetchDeliveryTargets();
  }, [fetchTags, fetchSurveys, fetchDeliveryTargets]);

  const questionCandidates = surveys.flatMap((survey) =>
    survey.questions.map((question) => ({
      key: question.id,
      label: `${survey.title} / ${question.label}`,
      type: question.type,
      options: question.options || [],
    }))
  );

  const handleTagToggle = (tagId: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter((id) => id !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const handleDeliveryTargetToggle = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      delivery_target_item_ids: prev.delivery_target_item_ids.includes(itemId)
        ? prev.delivery_target_item_ids.filter((id) => id !== itemId)
        : [...prev.delivery_target_item_ids, itemId],
    }));
  };

  const addFilterRow = () => {
    if (questionCandidates.length === 0) {
      toast.error('フィルターに使えるアンケート質問がありません');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      filters: [
        ...prev.filters,
        { key: questionCandidates[0].key, values: '', match: 'exact' },
      ],
    }));
  };

  const updateFilterRow = (index: number, patch: Partial<AttributeFilterRow>) => {
    setFormData((prev) => ({
      ...prev,
      filters: prev.filters.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const removeFilterRow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index),
    }));
  };

  const buildPayload = (): SendPayload | null => {
    const payload: SendPayload = {
      type: formData.type,
      title: formData.title,
      message: formData.message,
    };

    if (formData.message_type === 'image') {
      if (!formData.image_url) {
        toast.error('画像メッセージには画像URLが必要です');
        return null;
      }
      payload.message_type = 'image';
      payload.image_url = formData.image_url;
    } else if (formData.flex_message.trim()) {
      try {
        JSON.parse(formData.flex_message);
      } catch {
        toast.error('Flex Message JSON の形式が正しくありません');
        return null;
      }
      payload.message_type = 'flex';
      payload.flex_message = formData.flex_message;
    }

    if (formData.type === 'targeted') {
      payload.tags = formData.tags;
    }

    if (formData.type === 'multicast') {
      payload.delivery_target_item_ids = formData.delivery_target_item_ids;
    }

    const filters: SegmentCondition[] =
      formData.type === 'targeted'
        ? formData.filters
            .map((row) => ({
              key: row.key,
              values: row.values.split(',').map((v) => v.trim()).filter(Boolean),
              match: row.match,
            }))
            .filter((row) => row.values.length > 0)
        : [];

    if (filters.length > 0) {
      payload.attribute_filters = filters;
    }

    if (formData.scheduled_at) {
      const scheduled = new Date(formData.scheduled_at);
      if (Number.isNaN(scheduled.getTime())) {
        toast.error('予約日時の形式が正しくありません');
        return null;
      }
      if (scheduled.getTime() <= Date.now()) {
        toast.error('予約日時は現在より後の時刻を指定してください');
        return null;
      }
      payload.scheduled_at = scheduled.toISOString();
    }

    return payload;
  };

  const handleSend = async () => {
    if (!tenantStore.selectedTenant?.id) return;

    if (!formData.title || (!formData.message && formData.message_type === 'text')) {
      toast.error('タイトルとメッセージは必須です');
      return;
    }

    if (formData.type === 'targeted' && formData.tags.length === 0) {
      toast.error('ターゲット配信の場合はタグを選択してください');
      return;
    }

    if (formData.type === 'multicast' && formData.delivery_target_item_ids.length === 0) {
      toast.error('配信種別配信の場合は配信対象の項目を選択してください');
      return;
    }

    const payload = buildPayload();
    if (!payload) return;

    setSending(true);

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notifications/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(
          payload.scheduled_at
            ? `配信を予約しました (対象: ${result.target_count}件)`
            : `通知を送信しました (対象: ${result.target_count}件)`
        );

        setFormData((prev) => ({
          ...prev,
          title: '',
          message: '',
          image_url: '',
          flex_message: '',
          scheduled_at: '',
          tags: [],
          delivery_target_item_ids: [],
          filters: [],
        }));
      } else {
        const error = await response.json();
        toast.error(error.error || '送信に失敗しました');
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      toast.error('送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const openTestDialog = async () => {
    setTestDialogOpen(true);
    setSelectedGuestId('');

    if (!tenantStore.selectedTenant?.id) return;

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/guests`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data: Guest[] = await response.json();
        setGuests(data.filter((guest) => guest.type === 2));
      }
    } catch (error) {
      console.error('Failed to fetch guests:', error);
    }
  };

  const handleSendTest = async () => {
    if (!tenantStore.selectedTenant?.id || !selectedGuestId) return;

    if (!formData.title) {
      toast.error('テスト配信にはタイトルが必要です');
      return;
    }

    const testPayload: Record<string, unknown> = {
      guest_id: selectedGuestId,
      title: formData.title,
      message: formData.message,
    };
    if (formData.message_type === 'image' && formData.image_url) {
      testPayload.message_type = 'image';
      testPayload.image_url = formData.image_url;
    } else if (formData.flex_message.trim()) {
      testPayload.message_type = 'flex';
      testPayload.flex_message = formData.flex_message;
    }

    setTestLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantStore.selectedTenant.id}/notifications/test`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(testPayload),
        }
      );

      if (response.ok) {
        toast.success('テスト配信を送信しました');
        setTestDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.error || 'テスト配信に失敗しました');
      }
    } catch (error) {
      console.error('Failed to send test notification:', error);
      toast.error('テスト配信に失敗しました');
    } finally {
      setTestLoading(false);
    }
  };

  const filteredGuests = guests.filter((guest) =>
    (guest.name || '').toLowerCase().includes(guestSearch.toLowerCase()) ||
    (guest.line_uid || '').toLowerCase().includes(guestSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            プッシュ通知送信
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label>配信タイプ</Label>
              <RadioGroup
                value={formData.type}
                onValueChange={(value) =>
                  setFormData({ ...formData, type: value })
                }
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="broadcast" id="broadcast" />
                  <Label htmlFor="broadcast" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      全員に配信
                    </div>
                    <div className="text-sm text-gray-600">
                      LINE 公式アカウントの全フレンドに配信します (友だち追加していないユーザーには届きません)
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="multicast" id="multicast" />
                  <Label htmlFor="multicast" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <ListFilter className="h-4 w-4" />
                      配信種別で絞り込み
                    </div>
                    <div className="text-sm text-gray-600">
                      LIFF でユーザーが選択した配信種別の項目に該当するユーザーに配信します
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="targeted" id="targeted" />
                  <Label htmlFor="targeted" className="cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      タグで絞り込み
                    </div>
                    <div className="text-sm text-gray-600">
                      選択したタグを設定しているユーザーに配信します
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {formData.type === 'multicast' && (
              <div>
                <Label>配信対象の項目</Label>
                <p className="text-sm text-gray-600 mt-1 mb-2">
                  配信対象設定 LIFF でユーザーが選択している項目を選んでください。複数選択した場合はいずれかに該当するユーザーへ配信されます。
                </p>
                <div className="space-y-3">
                  {deliveryTargets.map((type) => (
                    <div key={type.id} className="border rounded-lg p-3">
                      <div className="text-sm font-medium mb-2">{type.name}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {type.items.map((item) => (
                          <label
                            key={item.id}
                            className="flex items-center gap-1.5 text-sm cursor-pointer"
                          >
                            <Checkbox
                              checked={formData.delivery_target_item_ids.includes(item.id)}
                              onCheckedChange={() => handleDeliveryTargetToggle(item.id)}
                            />
                            {item.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {deliveryTargets.length === 0 && (
                    <div className="text-sm text-gray-500">
                      有効な配信種別がありません。配信対象設定画面で作成してください。
                    </div>
                  )}
                </div>
              </div>
            )}

            {formData.type === 'targeted' && (
              <div>
                <Label>配信対象タグ</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant={
                        formData.tags.includes(tag.id) ? 'default' : 'outline'
                      }
                      className="cursor-pointer"
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      <div
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                {tags.length === 0 && (
                  <div className="text-sm text-gray-500 mt-2">
                    有効なタグがありません
                  </div>
                )}
              </div>
            )}

            {formData.type === 'targeted' && (
              <div>
                <Label>属性で絞り込み (アンケート回答)</Label>
              <p className="text-sm text-gray-600 mt-1 mb-2">
                アンケートの回答内容でさらに絞り込めます。条件間は AND、値間は OR で判定されます。
              </p>
              <div className="space-y-3">
                {formData.filters.map((row, index) => {
                  const candidate = questionCandidates.find((q) => q.key === row.key);
                  return (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row gap-2 items-start sm:items-end border rounded-lg p-3"
                    >
                      <div className="w-full sm:w-56 space-y-1">
                        <Label className="text-xs">質問</Label>
                        <Select
                          value={row.key}
                          onValueChange={(value) => updateFilterRow(index, { key: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="質問を選択" />
                          </SelectTrigger>
                          <SelectContent>
                            {questionCandidates.map((q) => (
                              <SelectItem key={q.key} value={q.key}>
                                {q.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="w-full sm:w-40 space-y-1">
                        <Label className="text-xs">一致方法</Label>
                        <Select
                          value={row.match}
                          onValueChange={(value) => updateFilterRow(index, { match: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">完全一致</SelectItem>
                            <SelectItem value="contains">部分一致</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex-1 w-full space-y-1">
                        <Label className="text-xs">値 (カンマ区切りでOR)</Label>
                        {candidate && candidate.options.length > 0 ? (
                          <div className="flex flex-wrap gap-2 py-1">
                            {candidate.options.map((opt) => {
                              const values = row.values.split(',').map((v) => v.trim()).filter(Boolean);
                              const checked = values.includes(opt);
                              return (
                                <label
                                  key={opt}
                                  className="flex items-center gap-1 text-sm cursor-pointer"
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(isChecked) => {
                                      const next = isChecked
                                        ? [...values, opt]
                                        : values.filter((v) => v !== opt);
                                      updateFilterRow(index, { values: next.join(',') });
                                    }}
                                  />
                                  {opt}
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <Input
                            value={row.values}
                            onChange={(e) => updateFilterRow(index, { values: e.target.value })}
                            placeholder="例: 東京,大阪"
                          />
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFilterRow(index)}
                        aria-label="条件を削除"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={addFilterRow}>
                <Plus className="h-4 w-4 mr-1" />
                条件を追加
              </Button>
              </div>
            )}

            <div>
              <Label htmlFor="title">タイトル</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="お知らせ"
                className="mt-2"
              />
            </div>

            <div>
              <Label>メッセージタイプ</Label>
              <RadioGroup
                value={formData.message_type}
                onValueChange={(value) =>
                  setFormData({ ...formData, message_type: value })
                }
                className="mt-2 flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="text" id="msg-text" />
                  <Label htmlFor="msg-text" className="cursor-pointer font-normal">
                    テキスト
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="image" id="msg-image" />
                  <Label htmlFor="msg-image" className="cursor-pointer font-normal">
                    画像
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="flex" id="msg-flex" />
                  <Label htmlFor="msg-flex" className="cursor-pointer font-normal">
                    Flex Message
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {formData.message_type === 'image' && (
              <div>
                <Label htmlFor="image_url">画像URL</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData({ ...formData, image_url: e.target.value })
                  }
                  placeholder="https://example.com/image.png"
                  className="mt-2"
                />
                <p className="text-sm text-gray-600 mt-1">
                  LINE からアクセス可能な HTTPS の画像 URL を指定してください。
                </p>
                {formData.image_url && (
                  <img
                    src={formData.image_url}
                    alt="プレビュー"
                    className="mt-2 max-h-40 rounded-md border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                    onLoad={(e) => {
                      (e.target as HTMLImageElement).style.display = 'block';
                    }}
                  />
                )}
              </div>
            )}

            {(formData.message_type === 'text' || formData.message_type === 'flex') && (
              <div>
                <Label htmlFor="message">メッセージ</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder={
                    formData.message_type === 'flex'
                      ? 'Flex Message の代替テキスト (省略可)'
                      : '配信するメッセージを入力してください'
                  }
                  rows={5}
                  className="mt-2"
                />
              </div>
            )}

            {formData.message_type === 'flex' && (
              <div>
                <Label htmlFor="flex_message">Flex Message JSON</Label>
                <Textarea
                  id="flex_message"
                  value={formData.flex_message}
                  onChange={(e) =>
                    setFormData({ ...formData, flex_message: e.target.value })
                  }
                  placeholder="LINE Flex Message JSON"
                  rows={5}
                  className="mt-2 font-mono text-sm"
                />
                <div className="text-sm text-gray-600 mt-1">
                  <a
                    href="https://developers.line.biz/flex-simulator/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Flex Message Simulator
                  </a>
                  で作成したJSONを貼り付けてください
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="scheduled_at">予約日時 (空欄なら即時送信)</Label>
              <Input
                id="scheduled_at"
                type="datetime-local"
                value={formData.scheduled_at}
                onChange={(e) =>
                  setFormData({ ...formData, scheduled_at: e.target.value })
                }
                className="mt-2 max-w-xs"
              />
              <p className="text-sm text-gray-600 mt-1">
                指定した日時になると自動的に配信されます (約1分単位で処理)。
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={openTestDialog}
              disabled={!formData.title}
              className="gap-2"
            >
              <FlaskConical className="h-4 w-4" />
              テスト配信
            </Button>

            <Button
              onClick={handleSend}
              disabled={
                sending ||
                !formData.title ||
                (formData.message_type === 'text' && !formData.message)
              }
              className="min-w-32"
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  送信中...
                </span>
              ) : formData.scheduled_at ? (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  予約する
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  送信
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>テスト配信</DialogTitle>
            <DialogDescription>
              作成中の内容を指定したゲストに送信して確認できます。ゲストにも通常どおり届くため注意してください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              value={guestSearch}
              onChange={(e) => setGuestSearch(e.target.value)}
              placeholder="ゲスト名 / LINE UID で検索"
            />

            <div className="max-h-64 overflow-auto rounded-md border divide-y">
              {filteredGuests.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  ゲストが見つかりません
                </div>
              )}
              {filteredGuests.map((guest) => (
                <button
                  key={guest.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent ${
                    selectedGuestId === guest.id ? 'bg-accent' : ''
                  }`}
                  onClick={() => setSelectedGuestId(guest.id)}
                >
                  <div className="font-medium">{guest.name || '(名前なし)'}</div>
                  <div className="text-xs text-muted-foreground">{guest.line_uid}</div>
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSendTest} disabled={!selectedGuestId || testLoading}>
              {testLoading ? '送信中...' : 'テスト配信を送る'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default NotificationSend;
